import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
	AppointmentApiModel,
	BookSlotApiInput,
	LocalityType,
	SearchCapabilitiesApiModel,
	TimeslotApiModel,
	TimeslotAvailabilityCountsApiModel,
	TimeslotAvailabilityGroupingApiModel,
	TimeslotLockApiModel,
} from '@visiba/api-vcs-client-v2';
import {
	clientBookableTimeLocksCreateLockMutation,
	clientBookableTimeLocksExtendLockMutation,
	clientBookableTimeLocksReleaseLockMutation,
	clientTimeslotsBookSlotMutation,
	clientTimeslotsGetAvailabilityCountsOptions,
	clientTimeslotsGetRescheduleAvailabilityCountsOptions,
	clientTimeslotsGetRescheduleTimeslotsOptions,
	clientTimeslotsGetSearchCapabilitiesOptions,
	clientTimeslotsRebookWithNewTimeslotMutation,
	clientTimeslotsSearchLocalityAvailabilityOptions,
	clientTimeslotsTimeslotOptions,
	clientTimeslotsTimeslotsOptions,
} from '@visiba/api-vcs-client-v2';
import { useCallback, useMemo, useRef } from 'react';

interface TimeSlotQueryParams {
	fromDate: string;
	toDate: string;
	fromTime: string;
	toTime: string;
	localityType?: LocalityType;
	searchContextId?: string;
	conditionId?: number;
	specialityId?: number;
	filterableUnitId?: number;
	resourceId?: number;
	physicalLocationId?: number;
	additionalParameters?: Record<string, string>;
}

export function useTimeSlots() {
	const queryClient = useQueryClient();

	// --- Mutations ---
	const createLockMutation = useMutation(clientBookableTimeLocksCreateLockMutation());
	const extendLockMutation = useMutation(clientBookableTimeLocksExtendLockMutation());
	const releaseLockMutation = useMutation(clientBookableTimeLocksReleaseLockMutation());
	const bookSlotMutation = useMutation(clientTimeslotsBookSlotMutation());
	const rebookMutation = useMutation(clientTimeslotsRebookWithNewTimeslotMutation());

	// Refs for stable references in useCallback deps
	const createLockRef = useRef(createLockMutation.mutateAsync);
	createLockRef.current = createLockMutation.mutateAsync;
	const extendLockRef = useRef(extendLockMutation.mutateAsync);
	extendLockRef.current = extendLockMutation.mutateAsync;
	const releaseLockRef = useRef(releaseLockMutation.mutateAsync);
	releaseLockRef.current = releaseLockMutation.mutateAsync;
	const bookSlotRef = useRef(bookSlotMutation.mutateAsync);
	bookSlotRef.current = bookSlotMutation.mutateAsync;
	const rebookRef = useRef(rebookMutation.mutateAsync);
	rebookRef.current = rebookMutation.mutateAsync;

	// --- Queries (imperative via fetchQuery) ---

	const getTimeSlots = useCallback(
		async (unitId: number, query: TimeSlotQueryParams): Promise<TimeslotApiModel[]> => {
			const response = await queryClient.fetchQuery(clientTimeslotsTimeslotsOptions({ path: { unitId }, query }));
			return response.Data ?? [];
		},
		[queryClient],
	);

	const getTimeSlot = useCallback(
		async (unitId: number, id: string): Promise<TimeslotApiModel> => {
			const response = await queryClient.fetchQuery(clientTimeslotsTimeslotOptions({ path: { unitId, id } }));
			return response.Data!;
		},
		[queryClient],
	);

	const getAvailabilityCounts = useCallback(
		async (unitId: number, query: Partial<TimeSlotQueryParams> = {}): Promise<TimeslotAvailabilityCountsApiModel> => {
			const response = await queryClient.fetchQuery(clientTimeslotsGetAvailabilityCountsOptions({ path: { unitId }, query }));
			return response.Data!;
		},
		[queryClient],
	);

	const getAvailableTimeSlots = useCallback(
		async (unitId: number, query: Partial<TimeSlotQueryParams> = {}): Promise<TimeslotAvailabilityGroupingApiModel> => {
			const response = await queryClient.fetchQuery(
				clientTimeslotsSearchLocalityAvailabilityOptions({ path: { unitId }, query: query as Record<string, unknown> }),
			);
			return response.Data!;
		},
		[queryClient],
	);

	const getSearchCapabilities = useCallback(
		async (unitId: number): Promise<SearchCapabilitiesApiModel> => {
			const response = await queryClient.fetchQuery(clientTimeslotsGetSearchCapabilitiesOptions({ path: { unitId } }));
			return response.Data!;
		},
		[queryClient],
	);

	const getRescheduleTimeSlots = useCallback(
		async (
			unitId: number,
			ticket: string,
			query: { fromDate?: string; toDate?: string; fromTime?: string; toTime?: string } = {},
		): Promise<TimeslotApiModel[]> => {
			const response = await queryClient.fetchQuery(clientTimeslotsGetRescheduleTimeslotsOptions({ path: { unitId, ticket }, query }));
			return response.Data ?? [];
		},
		[queryClient],
	);

	const getRescheduleAvailabilityCounts = useCallback(
		async (
			unitId: number,
			ticket: string,
			query: { fromDate?: string; toDate?: string; fromTime?: string; toTime?: string } = {},
		): Promise<TimeslotAvailabilityCountsApiModel> => {
			const response = await queryClient.fetchQuery(
				clientTimeslotsGetRescheduleAvailabilityCountsOptions({ path: { unitId, ticket }, query }),
			);
			return response.Data!;
		},
		[queryClient],
	);

	// --- Mutations (imperative via mutateAsync) ---

	const lockTimeSlot = useCallback(async (unitId: number, timeslotId: string): Promise<TimeslotLockApiModel> => {
		const response = await createLockRef.current({ path: { unitId, timeslotId } });
		return response.Data!;
	}, []);

	const updateLockedTimeSlot = useCallback(async (unitId: number, bookableTimeLockId: string): Promise<TimeslotLockApiModel> => {
		const response = await extendLockRef.current({ path: { unitId, bookableTimeLockId } });
		return response.Data!;
	}, []);

	const deleteLockTimeSlot = useCallback(async (unitId: number, bookableTimeLockId: string): Promise<void> => {
		await releaseLockRef.current({ path: { unitId, bookableTimeLockId } });
	}, []);

	const bookTimeSlot = useCallback(async (unitId: number, id: string, body: BookSlotApiInput): Promise<AppointmentApiModel> => {
		const response = await bookSlotRef.current({ path: { unitId, id }, body });
		return response.Data!;
	}, []);

	const rebookTimeSlot = useCallback(
		async (unitId: number, ticket: string, newTimeslotId: string, reschedulingReason?: string): Promise<AppointmentApiModel> => {
			const response = await rebookRef.current({
				path: { unitId, ticket },
				body: { NewTimeslotId: newTimeslotId, ReschedulingReason: reschedulingReason },
			});
			return response.Data!;
		},
		[],
	);

	return useMemo(
		() => ({
			getTimeSlots,
			getTimeSlot,
			getAvailabilityCounts,
			getAvailableTimeSlots,
			getSearchCapabilities,
			getRescheduleTimeSlots,
			getRescheduleAvailabilityCounts,
			lockTimeSlot,
			updateLockedTimeSlot,
			deleteLockTimeSlot,
			bookTimeSlot,
			rebookTimeSlot,
		}),
		[
			getTimeSlots,
			getTimeSlot,
			getAvailabilityCounts,
			getAvailableTimeSlots,
			getSearchCapabilities,
			getRescheduleTimeSlots,
			getRescheduleAvailabilityCounts,
			lockTimeSlot,
			updateLockedTimeSlot,
			deleteLockTimeSlot,
			bookTimeSlot,
			rebookTimeSlot,
		],
	);
}
